import React from "react";
import { Box, Button, Heading, Text, VStack } from "@chakra-ui/react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidMount() {
    // Keep this empty or remove it
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("ErrorBoundary caught:", error, errorInfo);

    // Auto-reload on ChunkLoadError, but prevent infinite loops (e.g., within 10 seconds)
    if (error?.name === "ChunkLoadError" || String(error).includes("ChunkLoadError")) {
      const lastReload = sessionStorage.getItem("chunk_load_error_reloaded_time");
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem("chunk_load_error_reloaded_time", now.toString());
        window.location.reload();
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleHardReset = () => {
    try {
      localStorage.removeItem("selected_user");
      const keys = Object.keys(localStorage);
      keys.forEach((k) => {
        if (k.startsWith("agent0_active_session_")) {
          localStorage.removeItem(k);
        }
      });
    } catch (_) {}
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minH="100vh"
          bg="gray.50"
          p={8}
        >
          <VStack spacing={5} maxW="500px" textAlign="center">
            <Heading size="lg" color="red.500">
              Something went wrong
            </Heading>
            <Text color="gray.600">
              The page encountered an unexpected error. You can try reloading or
              clearing cached data.
            </Text>
            {this.state.error && (
              <Box
                bg="red.50"
                border="1px solid"
                borderColor="red.200"
                borderRadius="md"
                p={3}
                w="100%"
                maxH="120px"
                overflowY="auto"
              >
                <Text fontSize="xs" color="red.700" fontFamily="mono" whiteSpace="pre-wrap">
                  {String(this.state.error)}
                </Text>
              </Box>
            )}
            <Button colorScheme="blue" onClick={this.handleReset}>
              Try Again
            </Button>
            <Button variant="outline" colorScheme="red" size="sm" onClick={this.handleHardReset}>
              Clear Cache &amp; Reload
            </Button>
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
